import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { api } from "../lib/api";
import { Plus, Sparkles } from "lucide-react";

export default function Gallery() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/looks");
        setLooks(data);
      } catch {
        toast.error("Could not load your gallery");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 sm:py-16">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6 fade-up">
          <div>
            <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Your Gallery</span>
            <h1 className="font-serif text-4xl sm:text-6xl mt-4 font-light leading-tight">
              The looks you've composed.
            </h1>
          </div>
          <Link
            to="/studio"
            data-testid="gallery-new"
            className="inline-flex items-center gap-3 bg-[#E3C2B0] text-[#0A0908] px-6 py-3 rounded-sm hover:bg-[#F0D4C5] transition-colors text-xs uppercase tracking-[0.3em]"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> New Look
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/5] shimmer rounded-sm" />
            ))}
          </div>
        ) : looks.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-sm py-32 text-center">
            <Sparkles className="w-8 h-8 text-[#E3C2B0] mx-auto mb-6" strokeWidth={1} />
            <h3 className="font-serif text-3xl mb-3">Your gallery awaits</h3>
            <p className="text-sm text-[#A8A6A1] mb-8 max-w-sm mx-auto">
              No looks yet. Compose your first one — it only takes a moment.
            </p>
            <Link
              to="/studio"
              data-testid="gallery-empty-cta"
              className="inline-flex items-center gap-3 bg-[#E3C2B0] text-[#0A0908] px-6 py-3 rounded-sm text-xs uppercase tracking-[0.3em]"
            >
              Begin
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {looks.map((look, i) => {
              const src = look.preview_image_base64
                ? `data:image/png;base64,${look.preview_image_base64}`
                : look.original_image_base64
                ? `data:image/png;base64,${look.original_image_base64}`
                : null;
              return (
                <Link
                  key={look.id}
                  to={`/look/${look.id}`}
                  data-testid={`gallery-item-${look.id}`}
                  className="relative aspect-[4/5] overflow-hidden rounded-sm group fade-up bg-[#1A1921]"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {src && (
                    <img
                      src={src}
                      alt={look.occasion}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F0E13] via-[#0F0E13]/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">{look.occasion}</span>
                    <div className="font-serif text-xl mt-2 text-[#F7F5F2] line-clamp-2">
                      {look.summary || "An editorial look"}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A6A1] mt-3">
                      {new Date(look.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
